import React, { useState, useMemo, useRef, useEffect } from 'react';

interface SearchableSelectOptionsProps {
  label?: string;
  placeholder: string;
  value: number;
  options: Array<{ pos: number; ar: string }>;
  onSelect: (value: number) => void;
  maxHeight?: number;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  style?: React.CSSProperties;
  countLabel?: string; // Label personnalisé pour le compteur (ex: "سورة", "عنصر", etc.)
}

/**
 * Composant de sélection avec recherche générique
 * Variante sans bouton clear et qui garde l'ancien choix
 * Adapté pour React web (tanzeel-app)
 */
export const SearchableSelectOptions: React.FC<SearchableSelectOptionsProps> = ({
  label = '',
  placeholder,
  value,
  options,
  onSelect,
  maxHeight = 300,
  disabled = false,
  loading = false,
  error = '',
  style = {},
  countLabel = 'عنصر'
}) => {
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSearchText(''); // Réinitialiser la recherche quand on ferme
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Filtrer les options en fonction du texte de recherche
  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) {
      return options;
    }
    const searchLower = searchText.toLowerCase().trim();
    return options.filter(option => 
      option.ar.toLowerCase().includes(searchLower)
    );
  }, [searchText, options]);

  const handleSelect = (option: { pos: number; ar: string }) => {
    setSearchText('');
    onSelect(option.pos);
    setShowDropdown(false);
    inputRef.current?.blur();
  };

  const handleFocus = () => {
    if (!disabled && !loading) {
      setShowDropdown(true);
    }
  };

  const handleChangeText = (text: string) => {
    setSearchText(text);
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  // Trouver le nom de l'option sélectionnée
  const selectedOptionName = useMemo(() => {
    const selected = options.find(opt => opt.pos === value);
    return selected ? selected.ar : '';
  }, [value, options]);

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: '8px', ...style }}>
      {label && (
        <label style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#6B7280',
          marginBottom: '6px',
          display: 'block',
          textAlign: 'right'
        }}>
          {label}
        </label>
      )}
      
      {/* Champ de recherche */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: disabled ? '#F3F4F6' : '#F9FAFB',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: showDropdown ? '#10b981' : '#E5E7EB',
        borderRadius: '8px',
        paddingLeft: '12px',
        paddingRight: '12px',
        transition: 'all 0.2s ease',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}>
        <input
          ref={inputRef}
          type="text"
          style={{
            flex: 1,
            height: '44px',
            fontSize: '15px',
            color: '#111827',
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            textAlign: 'right',
            cursor: disabled ? 'not-allowed' : 'text'
          }}
          placeholder={selectedOptionName || placeholder}
          value={searchText}
          onChange={(e) => handleChangeText(e.target.value)}
          onFocus={handleFocus}
          disabled={disabled || loading}
        />
        <button
          type="button"
          onClick={() => !disabled && !loading && setShowDropdown(!showDropdown)}
          disabled={disabled || loading}
          style={{
            padding: '4px',
            marginRight: '4px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span style={{
            fontSize: '16px',
            color: '#6B7280',
            transition: 'transform 0.2s ease',
            transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}>
            ▼
          </span>
        </button>
      </div>

      {/* Message de chargement */}
      {loading && (
        <div style={{
          padding: '6px',
          fontSize: '12px',
          color: '#6b7280',
          textAlign: 'center',
          marginTop: '4px'
        }}>
          جاري التحميل...
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div style={{
          padding: '6px',
          fontSize: '12px',
          color: '#dc2626',
          textAlign: 'center',
          marginTop: '4px'
        }}>
          {error}
        </div>
      )}

      {/* Liste déroulante des suggestions */}
      {showDropdown && !loading && !error && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#FFF',
          borderRadius: '8px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: '#E5E7EB',
          marginTop: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          {filteredOptions.length > 0 ? (
            <>
              <div style={{
                maxHeight: `${maxHeight}px`,
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                {filteredOptions.map((item) => (
                  <div
                    key={`${item.pos}-${item.ar}`}
                    onClick={() => handleSelect(item)}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      borderBottomWidth: '1px',
                      borderBottomStyle: 'solid',
                      borderBottomColor: '#F3F4F6',
                      backgroundColor: item.pos === value ? '#ECFDF5' : 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (item.pos !== value) {
                        e.currentTarget.style.backgroundColor = '#F9FAFB';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (item.pos !== value) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span style={{
                      flex: 1,
                      fontSize: '15px',
                      color: item.pos === value ? '#059669' : '#111827',
                      fontWeight: item.pos === value ? '600' : '400',
                      textAlign: 'right',
                      marginLeft: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.ar}
                    </span>
                    {item.pos === value && (
                      <span style={{
                        fontSize: '16px',
                        color: '#059669'
                      }}>
                        ✓
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {filteredOptions.length > 5 && (
                <div style={{
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  backgroundColor: '#F9FAFB',
                  borderTopWidth: '1px',
                  borderTopStyle: 'solid',
                  borderTopColor: '#E5E7EB'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center'
                  }}>
                    {filteredOptions.length} {countLabel}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: '24px',
              paddingBottom: '24px',
              paddingLeft: '16px',
              paddingRight: '16px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#6B7280',
                textAlign: 'center'
              }}>
                لا توجد نتائج
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelectOptions;
